/**
 * Document Uploader Component
 *
 * A drag-and-drop file upload component for PO documents.
 * Supports PDF and image files (PNG, JPEG, TIFF).
 *
 * Features:
 * - Drag and drop file upload
 * - Click to browse files
 * - File type validation
 * - Upload progress indicator
 * - File preview after selection
 */

import { useState, useRef, useCallback } from 'react';
import Button from '../common/Button';

/**
 * Allowed file types for upload
 * These correspond to the backend's multer configuration
 */
const ALLOWED_TYPES = [
  // Visual documents
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/tiff',
  // EDI files (often sent as text/plain)
  'text/plain',
  'application/EDI',
  'application/edi-x12',
  'application/edifact',
  'application/octet-stream',
  // Excel files
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  // CSV files
  'text/csv',
  'application/csv',
  // JSON files
  'application/json'
];

const ALLOWED_EXTENSIONS = '.pdf, .png, .jpg, .jpeg, .tiff, .xlsx, .xls, .csv, .json, .txt, .edi, .x12';

/**
 * DocumentUploader Component
 *
 * @param {Object} props - Component props
 * @param {Function} props.onFileSelect - Called when file(s) are selected
 * @param {boolean} [props.disabled=false] - Disable the uploader
 * @param {boolean} [props.multiple=false] - Allow multiple file selection
 *
 * @example
 * <DocumentUploader onFileSelect={(files) => console.log(files)} multiple />
 */
function DocumentUploader({ onFileSelect, disabled = false, multiple = false }) {
  // State
  const [isDragging, setIsDragging] = useState(false);  // Drag over state
  const [selectedFiles, setSelectedFiles] = useState([]); // Selected files
  const [error, setError] = useState(null);              // Error message

  // Ref to the hidden file input
  const fileInputRef = useRef(null);

  /**
   * Validate file type
   * @param {File} file - File to validate
   * @returns {boolean} True if valid
   */
  const validateFile = (file) => {
    // Allow files that match MIME type OR have valid extension (for EDI files)
    const extension = file.name.toLowerCase().split('.').pop();
    const validExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'tiff', 'xlsx', 'xls', 'csv', 'json', 'txt', 'edi', 'x12', '850', '855'];

    if (!ALLOWED_TYPES.includes(file.type) && !validExtensions.includes(extension)) {
      setError(`Invalid file type. Please upload: ${ALLOWED_EXTENSIONS}`);
      return false;
    }

    // Check file size (30MB max)
    const maxSize = 30 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File is too large. Maximum size is 30MB.');
      return false;
    }

    setError(null);
    return true;
  };

  /**
   * Handle file selection (from input or drop)
   * @param {FileList|File[]} files - Selected files
   */
  const handleFiles = useCallback((files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(file => validateFile(file));

    if (validFiles.length > 0) {
      if (multiple) {
        setSelectedFiles(prev => [...prev, ...validFiles]);
        onFileSelect?.([...selectedFiles, ...validFiles]);
      } else {
        setSelectedFiles([validFiles[0]]);
        onFileSelect?.(validFiles[0]);
      }
    }
  }, [onFileSelect, multiple, selectedFiles]);

  /**
   * Handle drag over event
   * Prevents default to allow drop
   */
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  /**
   * Handle drag leave event
   */
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  /**
   * Handle file drop
   */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  /**
   * Handle file input change
   */
  const handleInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  /**
   * Open file browser dialog
   */
  const openFileBrowser = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  /**
   * Clear all selected files
   */
  const clearFiles = () => {
    setSelectedFiles([]);
    setError(null);
    onFileSelect?.(multiple ? [] : null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Remove a specific file by index
   */
  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    onFileSelect?.(multiple ? newFiles : null);
  };

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted size (e.g., "2.5 MB")
   */
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="w-full">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_EXTENSIONS}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
        multiple={multiple}
      />

      {/* Drop zone */}
      <div
        onClick={openFileBrowser}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
          transition-colors duration-200
          ${isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${selectedFiles.length > 0 ? 'bg-green-50 border-green-400' : ''}
        `}
      >
        {/* No files selected - show upload prompt */}
        {selectedFiles.length === 0 && (
          <>
            <svg
              className={`mx-auto h-12 w-12 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>

            <p className="mt-4 text-lg font-medium text-gray-900">
              {isDragging ? 'Drop your file(s) here' : `Drag and drop your PO document${multiple ? 's' : ''}`}
            </p>

            <p className="mt-2 text-sm text-gray-500">
              or click to browse
            </p>

            <p className="mt-4 text-xs text-gray-400">
              Supported formats: PDF, Images, Excel, CSV, EDI, TXT (max 30MB){multiple ? ' - Select multiple files' : ''}
            </p>
          </>
        )}

        {/* Files selected - show file list */}
        {selectedFiles.length > 0 && (
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>

            <p className="mt-4 text-lg font-medium text-gray-900">
              {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
            </p>

            {/* File list */}
            {multiple && selectedFiles.length > 0 && (
              <div className="mt-3 max-h-32 overflow-auto">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-1 px-2 text-sm text-gray-600 hover:bg-gray-100 rounded"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="truncate max-w-xs">{file.name}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      className="ml-2 text-red-500 hover:text-red-700"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!multiple && selectedFiles[0] && (
              <p className="mt-1 text-sm text-gray-500">
                {selectedFiles[0].name} ({formatFileSize(selectedFiles[0].size)})
              </p>
            )}

            <div className="mt-4 flex justify-center gap-2">
              {multiple && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  Add More Files
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  clearFiles();
                }}
              >
                Clear All
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

export default DocumentUploader;
